/**
 * UranIA PA — Root route (US-009 → US-011, esteso US-008 Fase 6).
 *
 * Layout minimale: `<AiDisclosureBanner />` sticky AI Act art. 50
 * (US-011) + `<CutoverAnnouncementBanner />` sticky T-7gg cutover finale
 * (US-008 Fase 6 AC#1) + `<Outlet />` per le route figlie. Niente
 * assunzioni di utente autenticato — pagine protette saranno annidate
 * sotto route con `beforeLoad` guard a partire da US-012/US-013.
 *
 * Ordine sticky (visivo top→bottom):
 *   1. AiDisclosureBanner (compliance permanente AI Act art. 50);
 *   2. CutoverAnnouncementBanner (visibile sse utente in tenant target
 *      + giorno t∈[t_cutover-7gg, t_cutover]; auto-nascosto se
 *      `principal=null` o tenant non in `CUTOVER_SCHEDULE`).
 */

import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router';
import type { JSX } from 'react';

import { AppTopbar } from '../components/AppTopbar.tsx';
import { CutoverAnnouncementBanner } from '../components/cutover/CutoverAnnouncementBanner.tsx';
import { AiDisclosureBanner } from '../components/disclosure/AiDisclosureBanner.tsx';
import { InstitutionalBanner } from '../components/InstitutionalBanner.tsx';
import { selectIsAuthenticated, useAuthStore } from '../lib/auth/auth-store.ts';

// Rotte che gestiscono il proprio layout full-width: il <main> diventa
// un semplice wrapper senza ``container py-4``, cosi' le pagine possono
// disegnare colonne split-screen / hero / etc. Aggiungere nuove rotte
// qui solo quando vogliono *davvero* sovrascrivere il container PA-style.
const FULL_WIDTH_ROUTES: readonly string[] = ['/login'];

// Path su cui NON renderizzare l'AppTopbar globale.
// Solo ``/login`` (UX split-screen senza topbar). Tutte le altre pagine,
// inclusa l'editor OnlyOffice ``/workspace/mod5/<docId>``, mostrano la
// topbar globale per coerenza con il resto della SPA.
function shouldHideTopbar(pathname: string): boolean {
  if (pathname === '/login') return true;
  return false;
}

function RootLayout(): JSX.Element {
  const location = useLocation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isFullWidth = FULL_WIDTH_ROUTES.includes(location.pathname);
  const mainClassName = isFullWidth ? 'urania-app-shell__main--fluid' : 'container py-4';
  const showTopbar = isAuthenticated && !shouldHideTopbar(location.pathname);

  // La striscia istituzionale (Regione Siciliana · AdG FESR/FSE+) appare
  // sempre tranne sulla pagina di login pubblica (per coerenza con la
  // UX split-screen che non ha topbar). Su /workspace/mod5/<docId> il
  // Mod5Topbar non duplica piu' la stessa striscia (rimossa, vedi
  // components/workspace/Mod5Topbar.tsx).
  const showInstitutionalBanner = location.pathname !== '/login';

  return (
    <div className="urania-app-shell">
      <AiDisclosureBanner />
      <CutoverAnnouncementBanner />
      {showInstitutionalBanner && <InstitutionalBanner />}
      {showTopbar && <AppTopbar />}
      <main className={mainClassName} lang="it">
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
});
